import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router'
import { LoginStep, useAuthentication } from '@heidelbergmaterials/fcl-authclient'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWrench } from '@fortawesome/free-solid-svg-icons'
import { ThemeToggle } from './ThemeToggle'
import { AssetQuickSearch } from './AssetQuickSearch'

const navLinks = [
  { to: '/', label: 'Home', match: (path: string) => path === '/' },
  { to: '/assets', label: 'Assets', match: (path: string) => path.startsWith('/assets') },
  { to: '/plants', label: 'Plants', match: (path: string) => path.startsWith('/plants') },
  { to: '/clock-readings', label: 'Clock Readings', match: (path: string) => path.startsWith('/clock-readings') },
];

export function Navigation() {
  const { startLogoutFlow, decodedAccessToken, loginStep } = useAuthentication();
  const isLoggedIn = loginStep === LoginStep.Authenticated;
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  const tokenRole = decodedAccessToken?.role;
  const tokenRoles = (decodedAccessToken as ({ roles?: string[]; role?: string } | null))?.roles ?? [];
  const normalizedRoles = [tokenRole, ...tokenRoles]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.toLowerCase());
  const isFaimAdmin = normalizedRoles.some((value) =>
    value === 'admin'
    || value === 'globaladmin'
    || value === 'faimadmin'
    || value === 'faimglobaladmin'
  );

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 1);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-navigation-bg z-50 border-b border-border shadow-sm transition-colors duration-200">
        <div className="max-w-[1440px] mx-auto px-6 relative">
          {/* Green brand block — inside container, overlaps below nav */}
          <Link
            to="/"
            className="absolute top-0 left-0 bg-hc-green flex items-center justify-center shadow-md rounded-br-2xl z-[60] transition-all duration-400 ease-in-out"
            style={{ width: '220px', height: isScrolled ? '44px' : '100px', padding: '12px 24px' }}
          >
            <img src="/static/hclogo.png" alt="Heidelberg Materials" className="w-[150px] object-contain" />
          </Link>

          {/* Top row: user info + sign out */}
          <div className="flex items-center justify-end gap-3 px-4 transition-all duration-400 ease-in-out" style={{ opacity: isScrolled ? 0 : 1, height: isScrolled ? 0 : '32px', overflow: 'hidden' }}>
            {isLoggedIn && isFaimAdmin && (
              <Link
                to="/administration"
                title="Administration"
                className="text-foreground-faded hover:text-primary leading-none"
              >
                <FontAwesomeIcon icon={faWrench} />
              </Link>
            )}
            {isLoggedIn && decodedAccessToken && (
              <span className="text-sm text-primary font-medium">{decodedAccessToken.name}</span>
            )}
            <ThemeToggle />
            <button
              onClick={() => void startLogoutFlow()}
              className="text-sm text-foreground-faded hover:text-primary"
            >
              Sign out
            </button>
          </div>
          {/* Bottom row: nav links on right */}
          <div className="flex items-center h-11">
            {isLoggedIn && (
              <div className="ml-[230px]">
                <AssetQuickSearch />
              </div>
            )}
            <nav className="ml-auto flex items-center gap-2 px-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-base px-3 h-11 flex items-center border-b-2 transition-colors ${link.match(location.pathname) ? 'border-primary text-foreground font-medium' : 'border-transparent text-foreground-faded hover:text-primary hover:border-primary'}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </header>
      {/* Spacer to push content below fixed nav */}
      <div className="transition-all duration-400 ease-in-out" style={{ height: isScrolled ? '44px' : '100px' }} />
    </>
  );
}
